import {defineSlotRecipe} from '@chakra-ui/react3'

const spotlightBaseConfig = {
    position: 'absolute',
    insetInline: 0,
    insetBlockStart: 0,
    insetBlockEnd: '10%',
    zIndex: 'overlay',
    pointerEvents: 'none',
    _icon: {
        boxSize: '100%'
    }
}

const swatchBaseStyle = {
    position: 'relative',
    aspectRatio: '1/1',
    borderRadius: 'round',
    boxSize: '26px',
    overflow: 'hidden',
    _after: {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: 'round',
        boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.24) inset'
    },
    _groupActive: {
        outlineColor: 'border.brand',
        outlineStyle: 'solid',
        outlineWidth: '1px',
        outlineOffset: '2px'
    }
}

export default defineSlotRecipe({
    slots: [
        'root',
        'bgSurface',
        'spotlightLight',
        'spotlightDark',
        'contentContainer',
        'imagesContainer',
        'imageContainer',
        'imageContainerActive',
        'imageContainerInactive',
        'image',
        'swatchesContainer',
        'labelContainer',
        'label',
        'value',
        'swatches',
        'swatchTouchArea',
        'swatchLabel',
        'swatch',
        'multiColorSwatch',
        'separatorContainer',
        'separator'
    ],
    base: {
        root: {
            display: 'flex',
            paddingTop: {base: 'spacing-64', s: 'spacing-128'},
            paddingBottom: {base: 'spacing-40', s: 0},
            px: {base: 'spacing-24', s: 0},
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 12,
            position: 'relative'
        },
        bgSurface: {
            position: 'absolute',
            insetInline: 0,
            insetBlockEnd: 0,
            pointerEvents: 'none',
            opacity: {base: 0.5, s: 1},
            background: {
                base: 'radial-gradient(73.2% 109.67% at 50% 0%, #DFD6CF 0%, var(--module-bg-color) 100%)',
                s: 'radial-gradient(45.23% 406% at 50% 50%, #DFD6CF 0%, var(--module-bg-color) 100%)',
                // based on https://forum.figma.com/ask-the-community-7/wrong-gradient-behavior-with-opacity-36185
                // when figma is configured for radial-gradient with 0% opacity the browser doesn't reproduce the 0% color
                // thus we use mask for the opacity and here solid colors
                _dark: 'radial-gradient(65.06% 584% at 50% 34%, rgba(54, 54, 54, 1) 0%, var(--module-bg-color) 75%)'
            },
            maskImage: {
                _dark: 'radial-gradient(65.06% 584% at 50% 34%, transparent 0%, white 75%)'
            }
        },
        spotlightLight: {
            ...spotlightBaseConfig,
            display: {s: 'none', _dark: 'none'}
        },
        spotlightDark: {
            ...spotlightBaseConfig,
            display: {s: 'none', _light: 'none'}
        },
        contentContainer: {
            position: 'relative'
        },
        imagesContainer: {
            position: 'relative',
            inset: 0,
            height: {
                base: '390px',
                s: '530px'
            }
        },
        imageContainer: {
            position: 'absolute',
            inset: 0
        },
        imageContainerActive: {
            opacity: '1'
        },
        imageContainerInactive: {
            // use opacity to allow browser to preload the invisible images
            opacity: '0'
        },
        swatchesContainer: {
            position: {s: 'absolute'},
            insetInlineEnd: {s: 'spacing-90'},
            insetBlockEnd: {s: '83px'},
            display: 'flex',
            flexDirection: {base: 'column', s: 'row'},
            alignItems: 'center',
            gap: '12px',
            // need just a bit to be over the bg surface layer
            zIndex: 1,
            // to compute the correct max-width of swatches on mobile so it
            // doesn't extend more than necessary
            containerType: 'inline-size'
        },
        labelContainer: {
            display: {s: 'none'},
            textStyle: 'body.small',
            textAlign: 'center'
        },
        label: {
            color: 'text.secondary'
        },
        value: {
            color: 'text.primary'
        },
        swatches: {
            display: 'flex',
            justifyContent: 'center',
            flexDirection: {base: 'row', s: 'column'},
            flexWrap: {base: 'wrap', s: 'nowrap'},
            paddingBlock: {base: 'spacing-16', s: 'spacing-10'},
            paddingInline: {base: 'spacing-20', s: 'spacing-8'},
            borderRadius: {base: 'l', s: 'round'},
            backgroundColor: 'background.overlay.light-inverted'
        },
        swatchTouchArea: {
            position: 'relative',
            display: 'flex',
            boxSize: {base: '44px', s: 'auto'},
            padding: 'spacing-6',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 'round'
        },
        swatchLabel: {
            position: 'absolute',
            display: 'none',
            insetInlineEnd: 'calc(100% + 29px)',
            insetBlockStart: '50%',
            textAlign: 'end',
            transform: 'translateY(-50%)',
            textStyle: 'body.xsmall',
            color: 'text.primary',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            _groupHover: {
                display: {s: 'block'}
            }
        },
        swatch: swatchBaseStyle,
        multiColorSwatch: {
            ...swatchBaseStyle,
            bg: 'conic-gradient(from 180deg at 50% 50%, #FFA604 0deg, #FF4F45 72.0000010728836deg, #BA5DD2 144.0000021457672deg, #1299F5 216.00000858306885deg, #51CC26 288.0000042915344deg, #FFD203 360deg), #B0B0B2'
        },
        separatorContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingBlock: {s: 'spacing-8'},
            paddingInline: {base: 'spacing-8', s: 0}
        },
        separator: {
            borderColor: 'border.medium',
            width: {s: '26px'},
            height: {base: '26px', s: 'auto'}
        }
    },
    variants: {
        hasMultiColorImage: {
            false: {
                swatches: {
                    maxWidth: {
                        // 100% inline container width - swatches inline paddings,
                        // divide by the size of the swatch and round down
                        // then multiply by the swatch size and add the swatches inline paddings
                        base: 'calc(round(down, (100cqi - 40px) / 44px) * 44px + 40px)',
                        s: 'none'
                    }
                }
            },
            true: {
                swatches: {
                    maxWidth: {
                        // 100% inline container width - separator width - swatches inline paddings,
                        // divide by the size of the swatch and round down
                        // then multiply by the swatch size and add the separator width and swatches inline paddings
                        base: 'calc(round(down, (100cqi - 17px - 40px) / 44px) * 44px + 17px + 40px)',
                        s: 'none'
                    }
                }
            }
        },
        surfaceHeight: {
            20: {bgSurface: {height: {s: '20%'}}},
            30: {bgSurface: {height: {s: '30%'}}},
            40: {bgSurface: {height: {s: '40%'}}},
            50: {bgSurface: {height: {s: '50%'}}}
        },
        surfaceHeightMobile: {
            10: {bgSurface: {height: {base: '10%'}}},
            20: {bgSurface: {height: {base: '20%'}}},
            30: {bgSurface: {height: {base: '30%'}}},
            40: {bgSurface: {height: {base: '40%'}}}
        }
    },

    defaultVariants: {
        hasMultiColorImage: false,
        surfaceHeight: '20',
        surfaceHeightMobile: '20'
    }
})
